import { FC, useMemo } from 'react'

// eslint-disable-next-line camelcase
import { MemoryRouter, UNSAFE_LocationContext } from 'react-router'

import { useI18n } from '@tribeplatform/react-components/i18n'
import { SlateLayout } from '@tribeplatform/react-components/LayoutFrame'
import { PreviewLogoProvider } from '@tribeplatform/react-components/Logo'
import { useMeasure } from '@tribeplatform/react-ui-kit/hooks'

import { PageLayout } from '../../../Layout/PageLayout.js'
import {
  useCustomizer,
  useCustomizerSpace,
  type CustomizerPreviewMode,
} from '../hooks/index.js'
import { useCustomizerLogos } from '../hooks/use-customizer-logos.hook.js'
import { useCustomizerTemplate } from '../hooks/useCustomizerTemplate.js'
import { CustomizerActionType, PreviewSize } from '../types.js'

import { IFrame } from './IFrame.js'
import { PreviewContent } from './PreviewContent.js'

const Viewpoints = {
  sm: 430,
  md: 960,
  lg: 1500,
}

const getStylesBySize = (
  size: PreviewSize,
  width?: number,
  height?: number,
) => {
  if (!width) {
    return {
      height: 'calc(100vh - 80px)',
    }
  }

  const scale = width / Viewpoints[size]
  const ratio = Viewpoints[size] / width

  if (scale > 1) {
    return {
      height: 'calc(100vh - 80px)',
      width: size === 'lg' ? width : Viewpoints[size],
    }
  }

  return {
    msTransform: `scale(${scale})`,
    MozTransform: `scale(${scale})`,
    OTransform: `scale(${scale})`,
    WebkitTransform: `scale(${scale})`,
    transform: `scale(${scale})`,
    msTransformOrigin: '0 0',
    MozTransformOrigin: '0 0',
    OTransformOrigin: '0 0',
    WebkitTransformOrigin: '0 0',
    transformOrigin: '0 0',
    height: `max(100vh - 80px, ${height * ratio}px)`,
    minHeight: 0,
    width: `${width * ratio}px`,
  }
}

export const PreviewIFrame: FC<{
  previewSize: PreviewSize
  darkMode: boolean
  previewMode: CustomizerPreviewMode
  headerDisabled: boolean
  sidebar1Disabled: boolean
  navigations?: {
    Header: JSX.Element
    Sidebar1: JSX.Element
  }
}> = ({
  previewSize = 'lg',
  darkMode,
  previewMode,
  headerDisabled,
  sidebar1Disabled,
  navigations,
}) => {
  const {
    state: { activeBlock },
    dispatch,
  } = useCustomizer()
  const { space, layout, slateProps } = useCustomizerSpace()
  const { previewLayout } = useCustomizerTemplate()
  const { logo, mobileLogo } = useCustomizerLogos()

  const { languageDirection: direction } = useI18n()

  const [wrapperRef, { width, height }] = useMeasure<HTMLDivElement>()

  const finalLayout = useMemo(() => {
    if (previewMode === 'navigation') {
      if (headerDisabled && sidebar1Disabled) {
        return SlateLayout.noHeaderNoSidebar
      }

      if (headerDisabled) {
        return SlateLayout.sidebarNoHeader
      }

      if (sidebar1Disabled) {
        return SlateLayout.headerNoSidebar
      }

      return SlateLayout.headerSidebar
    }

    if (previewMode === 'template') {
      return previewLayout
    }

    return layout
  }, [headerDisabled, layout, previewLayout, previewMode, sidebar1Disabled])

  const style = useMemo(() => {
    return getStylesBySize(previewSize, width, height)
  }, [previewSize, width, height])

  return (
    <div
      ref={wrapperRef}
      style={{ maxHeight: 'calc(100vh - 100px)' }}
      className="w-full h-full"
    >
      <IFrame
        className="mx-auto rounded-base w-full h-full shadow-xl overflow-hidden"
        darkMode={darkMode}
        style={style}
      >
        {/* Its not possible to have nested routers with react-router v6
         Workaround is to reset context
         https://github.com/remix-run/react-router/issues/7375#issuecomment-975431736
         */}
        {/* eslint-disable-next-line react/jsx-pascal-case, camelcase */}
        <UNSAFE_LocationContext.Provider value={null}>
          <MemoryRouter
            initialEntries={[space?.address?.path || '/']}
            initialIndex={0}
          >
            <div className="relative" dir={direction}>
              <PreviewLogoProvider logo={logo} squareLogo={mobileLogo}>
                <PageLayout
                  forceLayout={
                    previewMode === 'navigation' ? finalLayout : undefined
                  }
                  layout={finalLayout}
                  navigations={navigations}
                  stickySidebar={
                    activeBlock?.action === 'edit' ? false : undefined
                  }
                  stickyHeader={
                    activeBlock?.action === 'edit' ? false : undefined
                  }
                >
                  <PreviewContent
                    previewMode={previewMode}
                    slateProps={slateProps}
                  />
                </PageLayout>
              </PreviewLogoProvider>
              {activeBlock?.action === 'edit' && (
                <div
                  className="absolute inset-0 bg-background-backdrop z-30 cursor-pointer"
                  onClick={() =>
                    dispatch({ type: CustomizerActionType.DEACTIVATE_BLOCK })
                  }
                />
              )}
            </div>
          </MemoryRouter>
          {/* eslint-disable-next-line camelcase */}
        </UNSAFE_LocationContext.Provider>
      </IFrame>
    </div>
  )
}
